<template>
  <q-drawer
    v-if="transaction_details != void 0"
    :model-value="show"
    side="right"
    :width="420"
    :breakpoint="1200"
    class="exchange-account-sidebar px-8"
    @update:model-value="$emit('update:show', $event)"
  >
    <!-- Header row  -->
    <q-item class="px-0 my-4">
      <q-item-section avatar>
        <q-avatar size="42px">
          <transition
            :key="transaction_details.asset_id"
            appear
            enter-active-class="animated bounceIn"
            leave-active-class="animated bounceOut"
            :duration="500"
          >
            <img
              :alt="transaction_details.asset_name || transaction_details.exchange_asset_name || transaction_details.exchange_symbol"
              :src="transaction_details.asset_icon"
            >
          </transition>
        </q-avatar>
      </q-item-section>
      <q-item-section>
        <q-item-label class="text-bold">
          {{
            transaction_details.asset_name
              ? transaction_details.asset_name
              : transaction_details.exchange_asset_name || transaction_details.exchange_symbol
          }}
        </q-item-label>
        <q-item-label
          caption
        >
          <q-chip
            square
            color="transparent"
            size="16px"
            dense
            class="text-weight-bold m-0 p-0"
            :class="{
              'price-tag-positive': amountFormattedIsPositive,
              'price-tag-negative': amountFormattedIsNegativeOrZero,
            }"
          >
            {{ transaction_details.amount_formatted }}

            <template v-if="!transaction_details.asset_id">
              <span class="q-ml-xs"> {{ transaction_details.exchange_symbol }}</span>
              <q-icon
                name="help_outline"
                class="q-ml-sm"
              >
                <q-tooltip
                  anchor="center left"
                  self="center right"
                  :delay="500"
                >
                  {{ i18n.transaction_details_asset_unknow }}<br>
                  {{ i18n.transaction_details_asset_unknow_contact }}
                </q-tooltip>
              </q-icon>
            </template>

            <template v-else>
              <q-tooltip
                anchor="center left"
                self="center right"
                :class="{
                  'price-tag-positive-bg': amountFormattedIsPositive,
                  'price-tag-positive-text': amountFormattedIsPositive,
                  'price-tag-negative-bg': amountFormattedIsNegativeOrZero,
                  'price-tag-negative-text': amountFormattedIsNegativeOrZero,
                }"
                :delay="500"
              >
                {{ transaction_details.amount_formatted }}
              </q-tooltip>
            </template>
          </q-chip>
        </q-item-label>
      </q-item-section>

      <q-item-section
        side
        class="flex"
        style="display: inline"
      >
        <q-btn
          unelevated
          flat
          round
          icon="more_vert"
        >
          <q-menu>
            <q-list
              v-if="transaction_context_menu_items.length > 0"
              dense
              class="transaction-context-menu q-pa-sm"
            >
              <template
                v-for="item in transaction_context_menu_items"
                :key="item.key"
              >
                <q-separator
                  v-if="item.type === 'separator'"
                  spaced
                  inset
                />
                <q-item
                  v-else
                  v-close-popup
                  clickable
                  @click="item.onClick && item.onClick()"
                >
                  <q-item-section
                    v-if="item.icon"
                    avatar
                  >
                    <q-icon :name="item.icon" />
                  </q-item-section>
                  <q-item-section>{{ item.label }}</q-item-section>
                </q-item>
              </template>
            </q-list>
          </q-menu>
        </q-btn>

        <q-btn
          unelevated
          flat
          round
          icon="close"
          @click="handleClose"
        />
      </q-item-section>
    </q-item>

    <q-separator class="q-mb-lg" />

    <div v-if="!loading">
      <VOnboardingWrapper
        ref="wrapper"
        :steps="onboardingSteps"
        :options="wrapperOptions"
      >
        <template #default="{ previous, next, step, isFirst, isLast, index }">
          <VOnboardingStep>
            <q-card
              class="q-pa-md onboarding-tooltip q-mt-md text-left"
              style="max-width: 420px;"
            >
              <div class="onboarding-tooltip-arrow" />
              <q-card-section class="q-pa-none">
                <div v-if="step.content">
                  <h3
                    v-if="step.content.title"
                    class="text-h6"
                  >
                    {{ step.content.title }}
                  </h3>
                  <div
                    v-if="step.content.description"
                    class="q-mt-sm"
                  >
                    <p>
                      {{ step.content.description }}
                    </p>
                  </div>
                </div>
              </q-card-section>
              <q-card-actions align="right">
                <span class="absolute right-0 bottom-full q-mb-sm q-mr-sm">{{ `${index + 1}/${onboardingSteps.length}` }}</span>
                <template v-if="!isFirst">
                  <q-btn
                    dense
                    outline
                    icon="chevron_left"
                    @click="previous"
                  />
                </template>
                <template v-if="isLast">
                  <q-btn
                    dense
                    :label="$q.lang.label.ok"
                    color="primary"
                    unelevated
                    @click="finishOnboarding"
                  />
                </template>
                <template v-else>
                  <q-btn
                    dense
                    color="primary"
                    unelevated
                    icon-right="chevron_right"
                    @click="next"
                  />
                </template>
              </q-card-actions>
            </q-card>
          </VOnboardingStep>
        </template>
      </VOnboardingWrapper>

      <!-- Kind -->
      <div class="mt-4 text-center">
        <template v-if="!transaction_details.justified && !transaction_details.kind">
          <q-btn
            :label="i18n.transactions_kinds_uncategorized"
            color="primary"
            unelevated
            dense
            size="sm"
            no-caps
            style="font-size: 13px"
            @click.stop="emitOpenKindDialog"
          />
        </template>
        <template v-else>
          <q-chip
            :color="transaction_details.kind_icon.color"
            :text-color="$q.dark.isActive ? transaction_details.kind_icon.color : 'black'"
            clickable
            flat
            square
            size="16px"
            class="q-ma-none transaction-kind-chip"
            :icon="transaction_details.kind_icon.class"
            @click.stop="emitOpenKindDialog"
          >
            {{
              transaction_details.trade_in_kind_label
                ? transaction_details.trade_in_kind_label
                : transaction_details.kind_label
            }}
          </q-chip>
        </template>
      </div>

      <q-separator class="q-my-lg" />

      <!-- Counter value -->
      <q-item dense clickable class="mt-4 bg-surface py-4" @click.stop="emitOpenCounterValue">
        <q-item-section avatar>
          <q-icon
            name="las la-euro-sign"
            size="28px"
            color="primary"
          />
        </q-item-section>

        <q-item-section>
          <q-item-label class="text-h6 text-bold">
            <span
              v-if="transaction_details.fiat_amount"
              class="modified-indicator"
            />

            {{ transaction_details.fiat_value_formatted }}
            <q-tooltip
              anchor="center left"
              self="center right"
              :delay="500"
              class="transaction-tooltip q-card shadow text-default q-pa-md q-menu"
            >
              {{ i18n.transaction_details_counter_value_info }}

              <span v-if="transaction_details.fiat_amount">
                <br>

                <span class="modified-indicator" />
                : {{ i18n.transaction_details_counter_value_exists }}
              </span>
            </q-tooltip>
          </q-item-label>
          <q-item-label caption>
            {{ i18n.transaction_details_counter_value_label }}
          </q-item-label>
        </q-item-section>

        <q-item-section side>
          <q-icon name="edit" />        
        </q-item-section>
      </q-item>

      <!-- Accounting details -->
      <q-item
        dense
        v-if="shared.workspace.is_business"
        class="mt-4 bg-surface py-4"
        clickable
        @click.stop="openAccountingDetails"
      >
        <q-item-section avatar>
          <q-icon
            name="sym_o_list_alt_check"
            size="28px"
            color="primary"
          />
        </q-item-section>

        <q-item-section>
          <q-item-label class="text-h6 text-bold">
            {{ i18n.transaction_details_title }}
          </q-item-label>
        </q-item-section>

        <q-item-section side>
          <q-icon name="chevron_right" />
        </q-item-section>
      </q-item>

      <q-separator class="q-my-lg" />
      <div class="q-pa-none">
          <q-card
            v-if="transaction_details.from_address || transaction_details.to_address"
            class="full-width q-mt-lg row q-pa-md items-center"
          >
            <div class="col-6 col-md-6 text-left">
              <span class="text-body2">
                {{ i18n.transaction_details_overview_sender }}
              </span>

              <q-item class="q-pl-none">
                <q-item-section>
                  <q-item-label lines="1">
                    <q-avatar
                      v-if="amountFormattedIsNegative"
                      size="xs"
                    >
                      <img
                        :alt="transaction_details.exchange_name"
                        :src="transaction_details.exchange_logo_url"
                      >
                    </q-avatar>

                    <q-avatar
                      v-else
                      size="xs"
                      icon="mdi-bank"
                      color="primary"
                      text-color="white"
                    />

                    <span class="q-pl-xs text-secondary">
                      {{ address_mappings_mapped(transaction_details.from_address) }}
                      <q-tooltip
                        anchor="center left"
                        self="center right"
                        class="transaction-tooltip q-card shadow q-pa-md q-menu text-default"
                        :delay="500"
                      >
                        <p class="q-mb-none">{{ transaction_details.from_address }}</p>
                      </q-tooltip>
                    </span>
                  </q-item-label>

                  <q-item-label
                    v-if="transaction_details.from_address"
                    class="q-pt-sm"
                  >
                    <ClipboardCopy
                      :content="transaction_details.from_address"
                      :message="i18n.transaction_details_overview_from_address_copied"
                    >
                      <span class="link-style text-primary">
                        {{ i18n.transaction_details_overview_copy }}
                      </span>
                    </ClipboardCopy>
                    <span class="text-light"> | </span>
                    <span
                      class="link-style text-primary"
                      @click="emitOpenAddressMapping(transaction_details.from_address)"
                    >
                      {{ i18n.transaction_details_overview_address_rename_action }}
                    </span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </div>

            <div class="col-6 col-md-6 justify-end">
              <span class="text-body2">
                {{ i18n.transaction_details_overview_recipient }}
              </span>
              <q-item class="q-pl-none">
                <q-item-section>
                  <q-item-label lines="1">
                    <q-avatar
                      v-if="amountFormattedIsPositive"
                      size="xs"
                    >
                      <img
                        :alt="transaction_details.exchange_name"
                        :src="transaction_details.exchange_logo_url"
                      >
                    </q-avatar>

                    <q-avatar
                      v-else
                      size="xs"
                      icon="mdi-bank"
                      color="primary"
                      text-color="white"
                    />

                    <span class="q-pl-xs text-secondary">
                      {{ address_mappings_mapped(transaction_details.to_address) }}
                      <q-tooltip
                        anchor="center left"
                        self="center right"
                        class="transaction-tooltip q-card shadow q-pa-md q-menu text-default"
                        :delay="500"
                      >
                        <p class="q-mb-none">{{ transaction_details.to_address }}</p>
                      </q-tooltip>
                    </span>
                  </q-item-label>

                  <q-item-label
                    v-if="transaction_details.from_address"
                    class="q-pt-sm"
                  >
                    <ClipboardCopy
                      :content="transaction_details.to_address"
                      :message="i18n.transaction_details_overview_to_address_copied"
                    >
                      <span class="link-style text-primary">
                        {{ i18n.transaction_details_overview_copy }}
                      </span>
                    </ClipboardCopy>
                    <span class="text-light"> | </span>
                    <span
                      class="link-style text-primary"
                      @click="emitOpenAddressMapping(transaction_details.to_address)"
                    >
                      {{ i18n.transaction_details_overview_address_rename_action }}
                    </span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </div>
          </q-card>

          <p class="sidebar-section-title">
            {{ i18n.transaction_details_overview_informations_title }}
          </p>

          <q-list class="q-pt-none">
            <q-item class="detailed-list-item items-center px-0">
              <q-item-section
                side
                class="q-pr-xs"
              >
                <q-item-label>
                  {{ i18n.transaction_details_overview_account_label }}
                </q-item-label>
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary">
                  <q-avatar
                    size="14px"
                    class="q-mr-xs"
                    style="vertical-align: middle"
                  >
                    <img
                      :alt="transaction_details.exchange_name"
                      :src="transaction_details.exchange_logo_url"
                    >
                  </q-avatar>
                  {{ truncate(transaction_details.exchange_account_name, 20) }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item class="detailed-list-item items-center px-0">
              <q-item-section
                side
                class="q-pr-xs"
              >
                <q-item-label>
                  {{ i18n.transaction_details_overview_date_label }}
                </q-item-label>
              </q-item-section>
              <q-item-section>
                <q-item-label class="inline-flex items-center text-secondary">
                  {{ transaction_details.datetime }}
                  <span
                    v-if="transaction_details.original_time"
                    class="modified-indicator modified-indicator--after"
                  >
                    <q-tooltip
                      anchor="top left"
                      self="bottom right"
                      :delay="500"
                      class="transaction-tooltip q-card shadow text-default q-pa-md q-menu"
                    >
                      {{ i18n.transaction_time_modified }}
                      <br>
                      {{ i18n.transaction_original_time }} {{ transaction_details.original_time_formatted }}
                    </q-tooltip>
                  </span>
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item
              v-if="transaction_details.blockchain_txid"
              class="detailed-list-item items-center px-0"
            >
              <q-item-section
                side
                class="q-pr-xs"
              >
                <q-item-label>
                  {{ i18n.transaction_details_overview_tx_hash_label }}
                </q-item-label>
              </q-item-section>
              <q-item-section class="transaction-hash-chip-section">
                <q-chip
                  dense
                  square
                  clickable
                  class="transaction-hash-chip"
                  :style="transactionHashChipStyle(transaction_details.blockchain_txid)"
                  @click.stop="emitFilterHash(transaction_details.blockchain_txid)"
                >
                  <span>{{ truncateMiddle(transaction_details.blockchain_txid) }}</span>
                  <q-tooltip
                    anchor="top middle"
                    self="bottom middle"
                    :delay="300"
                  >
                    {{ transaction_details.blockchain_txid }}
                  </q-tooltip>
                </q-chip>
              </q-item-section>
              <q-item-section
                side
                class="detailed-list-actions"
              >
                <q-btn
                  v-if="transaction_details.explorer_url"
                  unelevated
                  flat
                  round
                  color="primary"
                  size="sm"
                  target="_blank"
                  icon="las la-search"
                  :href="transaction_details.explorer_url"
                />
                <ClipboardCopy :content="transaction_details.blockchain_txid" />
              </q-item-section>
            </q-item>

            <q-item class="detailed-list-item items-center px-0">
              <q-item-section
                side
                class="q-pr-xs"
              >
                <q-item-label>
                  {{ i18n.transaction_details_overview_tx_id_label }}
                </q-item-label>
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary">
                  <span class="inline-block">
                    {{ truncate(transaction_details.movement_id, 16) }}
                    <q-tooltip
                      anchor="center left"
                      self="center right"
                      :delay="500"
                    >
                      {{ transaction_details.movement_id }}
                    </q-tooltip>
                  </span>
                </q-item-label>
              </q-item-section>
              <q-item-section
                side
                class="detailed-list-actions"
              >
                <q-btn
                  unelevated
                  flat
                  round
                  size="sm"
                  color="primary"
                  icon="fas fa-info"
                >
                  <q-tooltip
                    anchor="center right"
                    self="center left"
                    class="transaction-tooltip q-card shadow q-pa-md q-menu text-default"
                    style="width: 400px"
                    :delay="500"
                  >
                    <p class="q-mb-none">
                      {{ i18n.transaction_details_overview_tx_id_desc }}
                    </p>
                  </q-tooltip>
                </q-btn>
                <ClipboardCopy :content="transaction_details.movement_id" />
              </q-item-section>
            </q-item>

            <q-item
              v-if="transaction_details.manual"
              class="detailed-list-item items-center px-0 q-pt-lg"
            >
              <q-item-section
                side
                class="q-pr-xs"
              >
                <q-item-label class="text-italic">
                  <q-avatar
                    color="pastel-blue"
                    size="18px"
                    class="q-mr-sm"
                  >
                    <q-icon
                      name="las la-hand-paper"
                      color="black"
                      size="12px"
                    >
                      <q-tooltip
                        anchor="top left"
                        self="bottom right"
                        :delay="500"
                        class="transaction-tooltip q-card shadow text-default q-pa-md q-menu"
                      >
                        {{ i18n.transaction_details_overview_manual_desc }}
                      </q-tooltip>
                    </q-icon>
                  </q-avatar>
                  <span class="text-italic">
                    {{ i18n.transaction_details_overview_manual_desc }}
                  </span>
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>

          <q-separator class="q-my-lg" />

          <p class="sidebar-section-title">
            {{ i18n.transactions_documents_label }}
          </p>

          <q-list
            v-if="transaction_details.supporting_documents.length > 0"
            class="q-pb-md"
          >
            <q-item
              v-for="(doc, index) in transaction_details.supporting_documents"
              :key="index"
              class="detailed-list-item items-center px-0"
            >
              <q-item-section avatar>
                <img
                  class="file_icon"
                  width="14"
                  height="14"
                  :src="doc.icon"
                >
              </q-item-section>
              <q-item-section>
                <q-item-label
                  lines="1"
                  class="text-secondary"
                >
                  {{ doc.filename }}
                </q-item-label>
              </q-item-section>
              <q-item-section
                side
                class="detailed-list-actions"
              >
                <q-btn
                  flat
                  round
                  color="primary"
                  size="sm"
                  icon="las la-download"
                  download
                  target="_blank"
                  :href="doc.url"
                />
                <q-btn
                  unelevated
                  flat
                  round
                  size="sm"
                  color="negative"
                  icon="las la-trash-alt"
                  @click="transaction_details_documents_delete_confirm(doc)"
                />
              </q-item-section>
            </q-item>
          </q-list>

          <div class="q-pt-none">
            <Uploader
              :allowed-content-types="[
                'application/pdf',
                'image/png',
                'image/jpeg',
              ]"
              @signed_id="transaction_details_documents_add"
            />
          </div>

          <q-separator class="q-my-lg" />

          <p class="sidebar-section-title">
            {{ i18n.transaction_details_overview_note_label }}
          </p>

          <q-input
            v-model="transaction_details_note"
            class="full-width"
            outlined
            autogrow
            dense
            counter
            :loading="transactions_update_loading"
            maxlength="256"
            :placeholder="i18n.transaction_details_overview_note_placeholder"
            debounce="700"
            @update:model-value="(v) => emitUpdateTransaction({ note: v })"
          />
      </div>

      <div style="padding-bottom: 70px;" />
    </div>

    <q-inner-loading
      :showing="loading"
      style="opacity: 0.5"
    >
      <q-spinner
        size="50px"
        color="primary"
      />
    </q-inner-loading>

    <q-dialog
      v-if="shared.workspace.is_business"
      v-model="show_accounting_details"
    >
      <q-card
        class="accounting-dialog q-pa-md"
      >
        <q-card-section class="accounting-dialog__header row items-center q-pb-none">
          <div class="text-h5 text-bold">
            {{ i18n.transaction_details_accounting_title }}
          </div>
          <q-space />
          <q-btn
            icon="close"
            flat
            round
            dense
            v-close-popup
          />
        </q-card-section>

        <q-card-section class="accounting-dialog__content q-pt-md q-pa-md">

          <q-list class="q-pt-none">
            <q-item
              v-if="transaction_details.accounting_plan.name"
              class="detailed-list-item items-center px-0"
            >
              <q-item-section
                side
                class="q-pr-xs"
              >
                <q-item-label>
                  {{ i18n.transaction_details_accounting_plan_name }} :
                </q-item-label>
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary">
                  {{ transaction_details.accounting_plan.name }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item class="detailed-list-item items-center px-0">
              <q-item-section
                side
                class="q-pr-xs"
              >
                <q-item-label>
                  {{ i18n.transaction_details_accounting_plan_owner }} :
                </q-item-label>
              </q-item-section>
              <q-item-section>
                <q-item-label class="text-secondary">
                  {{ shared.wg.name }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>

          <div class="q-mt-md">
            <template v-if="transaction_details.accounting_action.link">
              <Link :href="transaction_details.accounting_action.link">
                <q-btn
                  color="primary"
                  outline
                  icon="edit"
                  :label="transaction_details.accounting_action.label"
                  unelevated
                  class="full-width"
                />
              </Link>
            </template>
            <template v-else>
              <span class="bg-transparent text-italic q-pa-none">
                {{ transaction_details.accounting_action.label }}
              </span>
            </template>
          </div>

          <q-banner
            v-if="transaction_details.accounting.ignore_transaction"
            class="bg-pastel-purple text-dark q-mb-lg q-mt-md"
            inline-actions
          >
            <template v-slot:avatar>
              <q-icon name="las la-ban" />
            </template>
            {{ i18n.transaction_details_accounting_tx_ignored }}
          </q-banner>

          <p
            v-if="
              transaction_details.accounting.ignore_transaction &&
                transaction_details.accounting.message
            "
            class="text-center"
          >
            {{ transaction_details.accounting.message }}
          </p>

          <div
            v-if="transaction_details.accounting.ignore_transaction == false"
            class="accounting-dialog__entries q-mt-md"
          >
            <template v-if="transaction_details.accounting?.mappings?.length > 0">
              <p class="sidebar-section-title">
                {{ i18n.transaction_details_accounting_mappings }}
              </p>
              <AccountingEntries
                :i18n="i18n"
                :accounting_details="transaction_details.accounting.mappings"
              />
            </template>

            <template v-if="transaction_details.accounting?.cglaes?.length > 0">
              <p class="sidebar-section-title">
                {{ i18n.transaction_details_accounting_cglaes }}
              </p>
              <AccountingEntries
                :i18n="i18n"
                :accounting_details="transaction_details.accounting.cglaes"
              />
            </template>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-section class="accounting-dialog__footer row q-col-gutter-md text-center">
          <div class="col-12">
            <span class="text-caption">{{ i18n.transaction_details_accounting_help_label }}</span>
            <q-chip
              clickable
              color="pastel-green"
              size="12px"
              class="q-ml-sm"
              text-color="black"
              icon="menu_book"
              :label="i18n.transaction_details_accounting_help_title"
              @click="$intercom.article_open('11829660')"
            />
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-drawer>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import { VOnboardingWrapper, VOnboardingStep } from 'v-onboarding';
import AccountingEntries from '@/pages/w/transactions/accounting_entries.vue';
import ClipboardCopy from '@/shared/clipboard_copy.vue';
import Uploader from '@/shared/uploader.vue';
import { generatePastelColorForHash, hslWithAlpha } from '@/pages/w/transactions/hash_utils.js';

export default {
  name: 'TransactionsDetailsSidebar',
  components: {
    AccountingEntries,
    ClipboardCopy,
    Link,
    Uploader,
    VOnboardingStep,
    VOnboardingWrapper,
  },
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    shared: {
      type: Object,
      required: true,
    },
    transaction_details: {
      type: Object,
      required: false,
    },
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    transactions_update_loading: {
      type: Boolean,
      default: false,
    },
    transactions_delete_loading: {
      type: Boolean,
      default: false,
    },
    transaction_context_menu_items: {
      type: Array,
      default: () => [],
    },
    supporting_documents_create_action_path: {
      type: String,
      required: true,
    },
    supporting_documents_destroy_action_path: {
      type: String,
      required: true,
    },
    address_mappings: {
      type: Object,
      required: true,
    },
    onboardingSteps: {
      type: Array,
      default: () => [],
    },
    wrapperOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: [
    'update:show',
    'close',
    'open-counter-value',
    'open-kind-dialog',
    'duplicate',
    'edit-amount',
    'restore-amount',
    'edit-time',
    'restore-time',
    'filter-hash',
    'update-transaction',
    'delete-transaction',
    'open-address-mapping',
    'onboarding-finish',
  ],
  data() {
    return {
      transaction_details_note: this.transaction_details?.note,
      transaction_details_documents_signed_ids: [],
      show_accounting_details: false,
    };
  },
  computed: {
    amountFormattedNormalized() {
      const formatted = this.transaction_details?.amount_formatted;
      if (formatted === undefined || formatted === null) {
        return '';
      }
      return String(formatted).trimStart();
    },
    amountFormattedIsPositive() {
      return this.amountFormattedNormalized.startsWith('+');
    },
    amountFormattedIsNegative() {
      const formatted = this.amountFormattedNormalized;
      return formatted.startsWith('-') || formatted.startsWith('−');
    },
    amountFormattedIsNegativeOrZero() {
      return !this.amountFormattedIsPositive;
    },
  },
  watch: {
    transaction_details(newValue) {
      this.transaction_details_note = newValue?.note;
    },
    show(newValue) {
      if (!newValue) {
        this.show_accounting_details = false;
      }
    },
  },
  methods: {
    finishOnboarding() {
      this.$emit('onboarding-finish');
      if (this.$refs.wrapper && typeof this.$refs.wrapper.finish === 'function') {
        this.$refs.wrapper.finish();
      }
    },
    emitOpenCounterValue() {
      this.$emit('open-counter-value', this.transaction_details);
    },
    emitOpenKindDialog() {
      this.$emit('open-kind-dialog', this.transaction_details);
    },
    openAccountingDetails() {
      this.show_accounting_details = true;
    },
    emitDuplicate() {
      this.$emit('duplicate', this.transaction_details.movement_id, this.transaction_details);
    },
    emitEditAmount() {
      this.$emit('edit-amount', this.transaction_details);
    },
    emitRestoreAmount() {
      this.$emit('restore-amount', this.transaction_details.movement_id);
    },
    emitEditTime() {
      this.$emit('edit-time', this.transaction_details);
    },
    emitRestoreTime() {
      this.$emit('restore-time', this.transaction_details.movement_id);
    },
    emitFilterHash(hash) {
      this.$emit('filter-hash', hash);
    },
    emitUpdateTransaction(params) {
      this.$emit('update-transaction', this.transaction_details, params);
    },
    emitDeleteTransaction() {
      this.$emit('delete-transaction', this.transaction_details);
    },
    emitOpenAddressMapping(address) {
      this.$emit('open-address-mapping', address, this.transaction_details);
    },
    handleClose() {
      this.$emit('close');
      this.$emit('update:show', false);
    },
    address_mappings_mapped(address) {
      if (!address) {
        return '---';
      }
      const name = this.address_mappings[address];
      if (name) {
        return name;
      }
      if (address.length > 16) {
        return `${address.substring(0, 6)}[...]${address.substring(address.length - 8)}`;
      }
      return address;
    },
    transactionHashChipStyle(hash) {
      const color = generatePastelColorForHash(hash);
      if (!hash || !color) {
        return {};
      }
      const isDarkMode = this.$q?.dark?.isActive;
      const styles = {
        borderColor: color,
        '--transaction-hash-chip-color': isDarkMode ? color : 'var(--text-default, #111)',
      };
      if (!isDarkMode) {
        styles.backgroundColor = hslWithAlpha(color, 0.45);
      }
      return styles;
    },
    truncate(str, n) {
      if (!str) return "";
      return str.length > n ? str.substr(0, n - 1) + "…" : str;
    },
    truncateMiddle(str, leading = 8, trailing = 8) {
      if (!str) {
        return '';
      }
      const ellipsis = '[...]';
      if (str.length <= leading + trailing + ellipsis.length) {
        return str;
      }
      const start = str.slice(0, leading);
      const end = str.slice(str.length - trailing);
      return `${start}${ellipsis}${end}`;
    },
    transaction_details_documents_add(blob, files_remaining) {
      this.transaction_details_documents_signed_ids.push(blob.signed_id);

      if (files_remaining === 0) {
        if (this.transaction_details_documents_signed_ids.length === 0) {
          return;
        }

        this.$inertia.post(
          this.supporting_documents_create_action_path,
          {
            movement_id: this.transaction_details.movement_id,
            supporting_documents: this.transaction_details_documents_signed_ids,
          },
          {
            preserveState: true,
            preserveScroll: true,
            replace: true,
            onStart: () => {
            },
            onSuccess: () => {
              this.$q.notify({
                message: this.$page.props.flash.success,
                type: "positive",
              });
            },
            onError: (error) => {
              console.log(error);
              this.$q.notify({
                message: this.$page.props.flash.alert,
                type: "negative",
              });
            },
            onFinish: () => {
              this.transaction_details_documents_signed_ids = [];
            }
          }
        );
      }
    },
    transaction_details_documents_delete_confirm(doc) {
      this.$q.dialog({
        title: this.i18n.supporting_documents_destroy_confirm_title,
        message: `${doc.filename} \n\n ${this.i18n.supporting_documents_destroy_confirm_message}`,
        cardClass: "whitespace-pre-line",
        cancel: true,
        color: "primary",
        persistent: true,
      }).onOk(() => {
        this.transaction_details_documents_delete(doc);
      });
    },
    transaction_details_documents_delete(doc) {
      this.$inertia.post(
        this.supporting_documents_destroy_action_path,
        { supporting_document_id: doc.id, },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'errors',
            'flash',
            'transaction_details',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (error) => {
            console.log(error);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          }
        }
      );
    },
  },
};
</script>

<style scoped>
.accounting-dialog {
  min-width: 520px;
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.accounting-dialog__header,
.accounting-dialog__footer {
  flex: 0 0 auto;
}

.accounting-dialog__content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.accounting-dialog__entries {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  padding-right: 4px;
}

.transaction-context-menu {
  min-width: 240px;
}

.file_icon {
  display: block;
  object-fit: fill;
  width: 14px;
  height: 14px;
}

.transaction-hash-chip {
  font-weight: 600;
  font-size: 11px;
  line-height: 1.1;
  padding: 2px 6px;
  color: var(--transaction-hash-chip-color, var(--text-default, #111)) !important;
}

.transaction-hash-chip-section {
  flex: 0 0 auto;
}

.body--dark .transaction-hash-chip {
  background-color: var(--chip-background) !important;
  color: var(--transaction-hash-chip-color, var(--text-default, #fff)) !important;
}

.detailed-list-actions {
  margin-left: auto;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  white-space: nowrap;
}
</style>
